import { ButtonBase, Dialog } from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";

export const useStyles = makeStyles(() => ({
    "@global": {
        /**
         * react-sortable-hoc helper element styling.
         * Without this, the helper can appear to “disappear” behind sticky headers (z-index),
         * or be visually unclear while dragging.
         */
        ".rm-sortable-helper": {
            zIndex: 999999,
            opacity: "1 !important",
            pointerEvents: "none",
            transform: "scale(1.03)",
            boxShadow: "0px 18px 48px rgba(0, 0, 0, 0.18)",
            borderRadius: "12px",
            background: "#ffffff",
        },
        // If the helper is a Chip, tighten its spacing and keep it readable.
        ".rm-sortable-helper.MuiChip-root": {
            boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.18)",
        },
    },
    DialogWrap: {
        '& .MuiDialog-paper': {
            '@media (max-width: 600px)': {
                margin: 0,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                borderRadius: 0,
            }
        }
    },
    Header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.5rem",
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: "#f5f6fa",
        borderBottom: "1px solid #e5e9f2",
    },
    ItemDesc: {
        display: "flex",
        alignItems: "center",
        gap: "1.5rem",
        flex: 1,
    },
    ItemDescImage: {
        width: "120px",
        height: "90px",
        borderRadius: "8px",
        objectFit: "cover",
    },
    ItemDescName: {
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "18px",
        lineHeight: "22px",
        color: "#271D1C",
        marginBottom: "0.5rem",
    },
    AddonsRaw: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.75rem 1rem",
        borderBottom: "1px solid #e5e9f2",
        transition: "background-color 0.2s",
        "&:hover": {
            backgroundColor: "#f5f6fa",
        },
        "&:last-child": {
            borderBottom: "none",
        },
        "& .MuiFormControlLabel-root": {
            margin: 0,
            flex: 1,
        },
        "& .MuiCheckbox-root": {
            padding: "0.5rem",
        },
        "& span": {
            marginLeft: "auto",
            fontFamily: "'Inter'",
            fontWeight: 600,
            fontSize: "16px",
            color: "#271D1C",
            "&:last-child": {
                marginRight: "1rem",
            },
        },
    },
    ItemDescPrice: {
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "24px",
        color: "#271D1C",
    },
    ItemQty: {
        background: "#FFFFFF",
        borderRadius: "8px",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        padding: "0.75rem",
        minWidth: "140px",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
    },
    ItemQtyMinus: {
        background: "#f5f6fa",
        "&:hover": {
            background: "#e5e9f2",
        },
    },
    ItemQtyPlus: {
        background: "#6E7187",
        color: "white",
        "&:hover": {
            background: "#5a5d6e",
        },
    },
    ItemQtyText: {
        fontFamily: "Inter",
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "24px",
        lineHeight: "29px",
        textAlign: "center",
        color: "#271D1C",
        minWidth: "40px",
    },
    Addons: {
        padding: "1.5rem",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: "300px",
        "& > div": {
            fontFamily: "'Inter'",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "20px",
            textTransform: "uppercase",
            color: "#6E7187",
            marginBottom: "1rem",
        }
    },
    OptionsList: {
        background: "#FFFFFF",
        border: "1px solid #e5e9f2",
        borderRadius: "12px",
        padding: "0.75rem",
        flex: 1,
        overflow: "auto",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
        maxHeight: "360px",
    },
    WizardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.25rem 0.25rem 0.75rem 0.25rem",
        color: "#6E7187",
        fontFamily: "'Inter'",
        fontWeight: 600,
        fontSize: "12px",
    },
    WizardActions: {
        marginTop: "0.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    StepButton: {
        background: "#6E7187",
        color: "#fff",
        borderRadius: "8px",
        padding: "0.5rem 1rem",
        fontFamily: "'Inter'",
        fontWeight: 600,
        fontSize: "12px",
        '&:disabled': {
            opacity: 0.5,
        }
    },
    OptionsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "8px",
        alignItems: "stretch",
        '@media (max-width: 900px)': {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        '@media (min-width: 1200px)': {
            gridTemplateColumns: "repeat(4, 1fr)",
        }
    },
    OptionItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #e5e9f2",
        borderRadius: "10px",
        padding: "0.5rem 0.75rem",
        background: "#fff",
        "& .MuiFormControlLabel-root": {
            margin: 0,
            flex: 1,
        },
        "& .MuiRadio-root": {
            padding: "0.25rem",
        },
    },
    OptionPrice: {
        marginLeft: "0.5rem",
        fontFamily: "'Inter'",
        fontWeight: 600,
        fontSize: "14px",
        color: "#271D1C",
    },
    AddonsList: {
        background: "#FFFFFF",
        border: "1px solid #e5e9f2",
        borderRadius: "12px",
        padding: "0.5rem",
        flex: 1,
        overflow: "auto",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
        maxHeight: "400px",
    },
    Notes: {
        padding: "1.5rem",
        "& > div": {
            fontFamily: "'Inter'",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "20px",
            textTransform: "uppercase",
            color: "#271D1C",
            marginBottom: "1rem",
        }
    },
    NotesArea: {
        width: "100%",
        background: "rgba(251, 253, 245, 0.75)",
        borderRadius: "12px",
        minHeight: "80px",
        maxHeight: "100px",
        padding: "0.75rem",
        border: "1px solid #e5e9f2",
        resize: "none",
        fontFamily: "'Inter'",
        fontSize: "14px",
        "&:focus": {
            outline: "none",
            borderColor: "#6E7187",
        }
    },
    NotesAreaLarge: {
        minHeight: "220px",
        maxHeight: "320px",
    },
    Footer: {
        padding: "1.5rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        borderTop: "1px solid #e5e9f2",
        background: "#FFFFFF",
    },
    FooterActions: {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
    },
    TotalText: {
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "17px",
        textTransform: "uppercase",
        color: "#271D1C",
    },
    TotalPrice: {
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 700,
        fontSize: "28px",
        lineHeight: "34px",
        textTransform: "uppercase",
        color: "#271D1C",
    },
    AddonsPrice: {
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "17px",
        textTransform: "uppercase",
        color: "#6E7187",
    },
    DialogAction: {
        background: "#6E7187",
        boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.07), 0px 4px 18px rgba(249, 98, 83, 0.25)",
        borderRadius: "8px",
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 700,
        fontSize: "14px",
        lineHeight: "17px",
        textAlign: "center",
        textTransform: "uppercase",
        color: "#FFFFFF",
        padding: "1rem 2.5rem",
        minWidth: "140px",
        transition: "background-color 0.2s",
        "&:hover": {
            background: "#5a5d6e",
        }
    },
    CancelAction: {
        background: "#FFFFFF",
        border: "1px solid #e5e9f2",
        borderRadius: "8px",
        fontFamily: "'Inter'",
        fontStyle: "normal",
        fontWeight: 800,
        fontSize: "14px",
        lineHeight: "17px",
        textAlign: "center",
        textTransform: "uppercase",
        color: "#6E7187",
        padding: "1rem 2.5rem",
        minWidth: "140px",
        transition: "background-color 0.2s, border-color 0.2s",
        "&:hover": {
            background: "#f5f6fa",
            borderColor: "#d6dbe7",
        },
    },
    ContentWrapper: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#f5f6fa",
        padding: 0,
    },
    ScrollableContent: {
        flex: 1,
        // Let inner panes (bundle option list) control their own scrolling.
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    }
}));

export const ItemDialog = withStyles(() => ({
    paper: {
        // width: "100vw",
        width: "800px",
        maxWidth: "100%",
        height: "90vh",
        maxHeight: "90vh",
        background: "#f5f6fa",
        display: "flex",
        flexDirection: "column",
        '@media (max-width: 600px)': {
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            margin: 0,
            borderRadius: 0,
        }
    },
}))(Dialog);

export const ItemSmallButton = withStyles(() => ({
    root: {
        width: "32px",
        height: "32px",
        borderRadius: "6px",
        transition: "all 0.2s",
    },
}))(ButtonBase);
